import React, { useState, useEffect } from 'react';
import { Calendar, Users, Clock, FileText, Edit2, Save, X, DollarSign, Plus, Trash2, Check, XCircle } from 'lucide-react';
import API_URL from '../../config';
import './ReservationCard.css';

const ReservationCard = ({
  reservation,
  isEditing,
  editForm,
  onEdit,
  onSave,
  onCancel,
  onChange,
  users = [],
  canEditPayments = false,
  canDeletePayments = false,
  onAddPayment,
  onUpdatePayment,
  onDeletePayment,
  onVerifyPayment,
  onRejectPayment,
  getAuthHeader
}) => {
  // Payment editing state
  const [editingPaymentId, setEditingPaymentId] = useState(null);
  const [calculatingPrice, setCalculatingPrice] = useState(false);

  // Recalculate total amount when dates or user change in edit mode
  useEffect(() => {
    if (!isEditing || !getAuthHeader) return;
    const { check_in_date, check_out_date, email } = editForm;
    if (!check_in_date || !check_out_date || check_in_date >= check_out_date) return;

    let cancelled = false;
    setCalculatingPrice(true);

    const fetchPrice = async () => {
      try {
        const endDateObj = new Date(check_out_date + 'T00:00:00');
        endDateObj.setDate(endDateObj.getDate() - 1);
        const endDate = endDateObj.toISOString().split('T')[0];

        const res = await fetch(
          `${API_URL}/api/calendar-pricing?startDate=${check_in_date}&endDate=${endDate}`,
          { headers: getAuthHeader() }
        );
        if (!res.ok || cancelled) return;

        const rows = await res.json();
        if (!Array.isArray(rows) || cancelled) return;

        const selectedUser = users.find(u => u.email === email);
        const isOwner = selectedUser?.owner === true;
        const total = rows.reduce((sum, row) => {
          const rate = isOwner
            ? parseFloat(row.owner_rate || 0)
            : parseFloat(row.renter_rate || 0);
          return sum + rate;
        }, 0);

        if (!cancelled && total > 0) {
          onChange({ total_amount: total.toFixed(2) });
        }
      } catch (err) {
        // silently fail — pricing data may not exist for these dates
      } finally {
        if (!cancelled) setCalculatingPrice(false);
      }
    };

    fetchPrice();
    return () => { cancelled = true; };
  }, [editForm?.check_in_date, editForm?.check_out_date, editForm?.email, isEditing]); // eslint-disable-line react-hooks/exhaustive-deps
  const [paymentEditForm, setPaymentEditForm] = useState({});
  const [isAddingPayment, setIsAddingPayment] = useState(false);
  const [newPaymentForm, setNewPaymentForm] = useState({
    amount: '',
    payment_date: new Date().toISOString().split('T')[0],
    payment_method: '',
    notes: ''
  });
  const [paymentLoading, setPaymentLoading] = useState(false);

  // Start editing a payment
  const handleEditPayment = (payment) => {
    setEditingPaymentId(payment.id);
    setPaymentEditForm({
      amount: payment.amount,
      payment_date: payment.payment_date?.split('T')[0] || '',
      payment_method: payment.payment_method || '',
      notes: payment.notes || ''
    });
  };

  // Cancel editing
  const handleCancelEditPayment = () => {
    setEditingPaymentId(null);
    setPaymentEditForm({});
  };

  // Save payment edit
  const handleSavePayment = async () => {
    if (!onUpdatePayment) return;
    setPaymentLoading(true);
    try {
      const result = await onUpdatePayment(editingPaymentId, paymentEditForm);
      if (result.success) {
        setEditingPaymentId(null);
        setPaymentEditForm({});
      }
    } finally {
      setPaymentLoading(false);
    }
  };

  // Delete payment
  const handleDeletePayment = async (paymentId) => {
    if (!onDeletePayment) return;
    if (!window.confirm('Are you sure you want to delete this payment?')) return;

    setPaymentLoading(true);
    try {
      await onDeletePayment(paymentId);
    } finally {
      setPaymentLoading(false);
    }
  };

  // Add new payment
  const handleAddPayment = async () => {
    if (!onAddPayment) return;
    if (!newPaymentForm.amount) {
      alert('Amount is required');
      return;
    }

    setPaymentLoading(true);
    try {
      const result = await onAddPayment(reservation.id, newPaymentForm);
      if (result.success) {
        setIsAddingPayment(false);
        setNewPaymentForm({
          amount: '',
          payment_date: new Date().toISOString().split('T')[0],
          payment_method: '',
          notes: ''
        });
      }
    } finally {
      setPaymentLoading(false);
    }
  };

  // Verify a pending payment
  const handleVerifyPayment = async (paymentId) => {
    if (!onVerifyPayment) return;
    setPaymentLoading(true);
    try {
      await onVerifyPayment(paymentId);
    } finally {
      setPaymentLoading(false);
    }
  };

  // Reject a pending payment
  const handleRejectPayment = async (paymentId) => {
    if (!onRejectPayment) return;
    const reason = window.prompt('Enter rejection reason (optional):');
    if (reason === null) return; // User cancelled

    setPaymentLoading(true);
    try {
      await onRejectPayment(paymentId, reason);
    } finally {
      setPaymentLoading(false);
    }
  };

  return (
    <div className="reservation-card-container">
      <div className="reservation-card-modern">
        <div className="reservation-card-header">
          {/* Reservation ID */}
          <div className="reservation-id-section">
            {isEditing ? (
              <input
                type="text"
                value={editForm.reservation_number}
                onChange={(e) => onChange({ reservation_number: e.target.value })}
                className="reservation-id-input"
              />
            ) : (
              <div className="reservation-id">
                {reservation.reservation_number || `#${reservation.id}`}
              </div>
            )}
            {isEditing ? (
              <div className="edit-actions-inline">
                <button
                  onClick={onSave}
                  className="btn btn-success"
                >
                  <Save size={20} />
                  Save
                </button>
                <button
                  onClick={onCancel}
                  className="btn btn-neutral"
                >
                  <X size={20} />
                  Cancel
                </button>
              </div>
            ) : onEdit && (
              <button
                onClick={onEdit}
                className="btn btn-primary"
              >
                <Edit2 size={20} />
                Edit
              </button>
            )}
          </div>
        </div>

        <div className="reservation-card-content">
          {/* User Name + Email */}
          {isEditing && users.length > 0 ? (
            <div className="info-row">
              <span className="info-label">User:</span>
              <select
                value={editForm.email || ''}
                onChange={(e) => onChange({ email: e.target.value })}
                className="edit-input"
              >
                <option value="">— select user —</option>
                {users
                  .filter(u => u.is_active)
                  .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
                  .map(u => (
                    <option key={u.id} value={u.email}>
                      {u.name} ({u.email})
                    </option>
                  ))}
              </select>
            </div>
          ) : (
            <>
              {reservation.user_name && (
                <div className="info-row">
                  <span className="info-label">Name:</span>
                  <span className="info-value">{reservation.user_name}</span>
                </div>
              )}
              <div className="info-row">
                <span className="info-label">Email:</span>
                <span className="info-value">{reservation.email}</span>
              </div>
            </>
          )}

          {/* Check-in and Check-out */}
          <div className="info-row dates-row">
            <div className="date-group">
              <Calendar size={20} className="icon" />
              <span className="info-label">Check-in:</span>
              {isEditing ? (
                <input
                  type="date"
                  value={editForm.check_in_date}
                  onChange={(e) => onChange({ check_in_date: e.target.value })}
                  className="date-input"
                />
              ) : (
                <span className="info-value">{new Date(reservation.check_in_date).toLocaleDateString()}</span>
              )}
            </div>
            <div className="date-group">
              <Calendar size={20} className="icon" />
              <span className="info-label">Check-out:</span>
              {isEditing ? (
                <input
                  type="date"
                  value={editForm.check_out_date}
                  onChange={(e) => onChange({ check_out_date: e.target.value })}
                  className="date-input"
                />
              ) : (
                <span className="info-value">{new Date(reservation.check_out_date).toLocaleDateString()}</span>
              )}
            </div>
          </div>

          {/* Type and Year */}
          <div className="info-row metadata-row">
            <div className="metadata-group">
              <Users size={20} className="icon" />
              <span className="info-label">Type:</span>
              {isEditing ? (
                <input
                  type="text"
                  value={editForm.type}
                  onChange={(e) => onChange({ type: e.target.value })}
                  className="text-input"
                />
              ) : (
                <span className="info-value">{reservation.type || 'N/A'}</span>
              )}
            </div>
            <div className="metadata-group">
              <Clock size={20} className="icon" />
              <span className="info-label">Year:</span>
              {isEditing ? (
                <input
                  type="number"
                  value={editForm.year}
                  onChange={(e) => onChange({ year: e.target.value })}
                  className="year-input"
                />
              ) : (
                <span className="info-value">{reservation.year || 'N/A'}</span>
              )}
            </div>
          </div>

          {/* Description */}
          {(isEditing || reservation.description) && (
            <div className="info-row description-row">
              <FileText size={20} className="icon" />
              <div className="description-content">
                <span className="info-label">Description:</span>
                {isEditing ? (
                  <textarea
                    value={editForm.description}
                    onChange={(e) => onChange({ description: e.target.value })}
                    className="textarea-input"
                    rows={2}
                  />
                ) : (
                  <span className="info-value">{reservation.description}</span>
                )}
              </div>
            </div>
          )}

          {/* Notes */}
          {(isEditing || reservation.notes) && (
            <div className="info-row notes-row">
              <FileText size={20} className="icon" />
              <div className="notes-content">
                <span className="info-label">Notes:</span>
                {isEditing ? (
                  <textarea
                    value={editForm.notes}
                    onChange={(e) => onChange({ notes: e.target.value })}
                    className="textarea-input"
                    rows={2}
                  />
                ) : (
                  <span className="info-value">{reservation.notes}</span>
                )}
              </div>
            </div>
          )}

          {/* Financial Information */}
          {(isEditing || reservation.total_amount > 0 || reservation.total_price > 0 || reservation.amount_paid > 0) && (
            <div className="info-row financial-row">
              <DollarSign size={20} className="icon" />
              <div className="financial-content">
                <div className="financial-item">
                  <span className="info-label">Total:</span>
                  {isEditing ? (
                    <>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={editForm.total_amount}
                        onChange={(e) => onChange({ total_amount: e.target.value })}
                        className="amount-input"
                        placeholder={calculatingPrice ? 'Calculating...' : '0.00'}
                        disabled={calculatingPrice}
                      />
                      {calculatingPrice && (
                        <small style={{ color: '#888', marginLeft: '6px' }}>Fetching rate...</small>
                      )}
                    </>
                  ) : (
                    <span className="info-value">
                      ${(reservation.total_amount != null ? parseFloat(reservation.total_amount) : reservation.total_price)?.toFixed(2) || '0.00'}
                    </span>
                  )}
                </div>
                <div className="financial-item">
                  <span className="info-label">Paid:</span>
                  <span className="info-value amount-paid">${reservation.amount_paid?.toFixed(2) || '0.00'}</span>
                </div>
                <div className="financial-item">
                  <span className="info-label">Owed:</span>
                  {(() => {
                    const total = reservation.total_amount != null ? parseFloat(reservation.total_amount) : 0;
                    const paid = parseFloat(reservation.amount_paid) || 0;
                    const owed = Math.max(0, total - paid);
                    return (
                      <span className={`info-value ${owed > 0 ? 'amount-owed' : 'amount-paid'}`}>
                        ${owed.toFixed(2)}
                      </span>
                    );
                  })()}
                </div>
              </div>
            </div>
          )}

          {/* Payments Table - Show if there are payments OR if user can add payments */}
          {(reservation.payments?.length > 0 || canEditPayments) && (
            <div className="payments-section">
              <div className="payments-header">
                <div className="payments-header-left">
                  <DollarSign size={18} className="icon" />
                  <span className="info-label">Payment History</span>
                </div>
                {canEditPayments && !isAddingPayment && (
                  <button
                    onClick={() => setIsAddingPayment(true)}
                    className="btn btn-sm btn-success"
                    disabled={paymentLoading || editingPaymentId !== null}
                  >
                    <Plus size={16} />
                    Add Payment
                  </button>
                )}
              </div>

              {(reservation.payments?.length > 0 || isAddingPayment) ? (
                <table className="payments-table">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Amount</th>
                      <th>Method</th>
                      <th>Status</th>
                      <th>Notes</th>
                      <th>Recorded By</th>
                      {canEditPayments && <th>Actions</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {/* Add Payment Form Row */}
                    {isAddingPayment && (
                      <tr className="payment-form-row">
                        <td>
                          <input
                            type="date"
                            value={newPaymentForm.payment_date}
                            onChange={(e) => setNewPaymentForm({...newPaymentForm, payment_date: e.target.value})}
                            className="payment-input payment-date-input"
                            disabled={paymentLoading}
                          />
                        </td>
                        <td>
                          <input
                            type="number"
                            step="0.01"
                            min="0"
                            placeholder="0.00"
                            value={newPaymentForm.amount}
                            onChange={(e) => setNewPaymentForm({...newPaymentForm, amount: e.target.value})}
                            className="payment-input payment-amount-input"
                            disabled={paymentLoading}
                          />
                        </td>
                        <td>
                          <select
                            value={newPaymentForm.payment_method}
                            onChange={(e) => setNewPaymentForm({...newPaymentForm, payment_method: e.target.value})}
                            className="payment-input payment-method-select"
                            disabled={paymentLoading}
                          >
                            <option value="">Select...</option>
                            <option value="Check">Check</option>
                            <option value="Cash">Cash</option>
                            <option value="Venmo">Venmo</option>
                            <option value="Zelle">Zelle</option>
                            <option value="Credit Card">Credit Card</option>
                            <option value="Other">Other</option>
                          </select>
                        </td>
                        <td className="status-cell">-</td>
                        <td>
                          <input
                            type="text"
                            placeholder="Notes..."
                            value={newPaymentForm.notes}
                            onChange={(e) => setNewPaymentForm({...newPaymentForm, notes: e.target.value})}
                            className="payment-input payment-notes-input"
                            disabled={paymentLoading}
                          />
                        </td>
                        <td className="recorded-by-cell">-</td>
                        <td className="payment-actions-cell">
                          <button
                            onClick={handleAddPayment}
                            className="btn btn-sm btn-success"
                            disabled={paymentLoading}
                          >
                            <Save size={14} />
                          </button>
                          <button
                            onClick={() => setIsAddingPayment(false)}
                            className="btn btn-sm btn-neutral"
                            disabled={paymentLoading}
                          >
                            <X size={14} />
                          </button>
                        </td>
                      </tr>
                    )}

                    {/* Existing Payment Rows */}
                    {reservation.payments?.map((payment) => (
                      <tr key={payment.id} className={editingPaymentId === payment.id ? 'editing' : ''}>
                        {editingPaymentId === payment.id ? (
                          <>
                            <td>
                              <input
                                type="date"
                                value={paymentEditForm.payment_date}
                                onChange={(e) => setPaymentEditForm({...paymentEditForm, payment_date: e.target.value})}
                                className="payment-input payment-date-input"
                                disabled={paymentLoading}
                              />
                            </td>
                            <td>
                              <input
                                type="number"
                                step="0.01"
                                min="0"
                                value={paymentEditForm.amount}
                                onChange={(e) => setPaymentEditForm({...paymentEditForm, amount: e.target.value})}
                                className="payment-input payment-amount-input"
                                disabled={paymentLoading}
                              />
                            </td>
                            <td>
                              <select
                                value={paymentEditForm.payment_method}
                                onChange={(e) => setPaymentEditForm({...paymentEditForm, payment_method: e.target.value})}
                                className="payment-input payment-method-select"
                                disabled={paymentLoading}
                              >
                                <option value="">Select...</option>
                                <option value="Check">Check</option>
                                <option value="Cash">Cash</option>
                                <option value="Venmo">Venmo</option>
                                <option value="Zelle">Zelle</option>
                                <option value="Credit Card">Credit Card</option>
                                <option value="Other">Other</option>
                              </select>
                            </td>
                            <td className="status-cell">
                              <span className={`payment-status-badge status-${payment.status || 'verified'}`}>
                                {payment.status === 'pending' ? 'Pending' : payment.status === 'rejected' ? 'Rejected' : 'Verified'}
                              </span>
                            </td>
                            <td>
                              <input
                                type="text"
                                value={paymentEditForm.notes}
                                onChange={(e) => setPaymentEditForm({...paymentEditForm, notes: e.target.value})}
                                className="payment-input payment-notes-input"
                                disabled={paymentLoading}
                              />
                            </td>
                            <td className="recorded-by-cell">{payment.created_by_name || '-'}</td>
                            <td className="payment-actions-cell">
                              <button
                                onClick={handleSavePayment}
                                className="btn btn-sm btn-success"
                                disabled={paymentLoading}
                              >
                                <Save size={14} />
                              </button>
                              <button
                                onClick={handleCancelEditPayment}
                                className="btn btn-sm btn-neutral"
                                disabled={paymentLoading}
                              >
                                <X size={14} />
                              </button>
                            </td>
                          </>
                        ) : (
                          <>
                            <td>{new Date(payment.payment_date).toLocaleDateString()}</td>
                            <td className={`amount-cell ${payment.status === 'pending' ? 'amount-pending' : payment.status === 'rejected' ? 'amount-rejected' : ''}`}>
                              ${parseFloat(payment.amount).toFixed(2)}
                            </td>
                            <td>{payment.payment_method || '-'}</td>
                            <td>
                              <span className={`payment-status-badge status-${payment.status || 'verified'}`}>
                                {payment.status === 'pending' ? 'Pending' : payment.status === 'rejected' ? 'Rejected' : 'Verified'}
                              </span>
                            </td>
                            <td className={payment.status === 'rejected' ? 'rejected-note' : ''}>
                              {payment.status === 'rejected' && payment.rejection_reason
                                ? `${payment.notes || '-'} (Rejected: ${payment.rejection_reason})`
                                : (payment.notes || '-')}
                            </td>
                            <td>{payment.created_by_name || '-'}</td>
                            {canEditPayments && (
                              <td className="payment-actions-cell">
                                {payment.status === 'pending' ? (
                                  <>
                                    <button
                                      onClick={() => handleVerifyPayment(payment.id)}
                                      className="btn btn-sm btn-success"
                                      disabled={paymentLoading || editingPaymentId !== null || isAddingPayment}
                                      title="Verify payment"
                                    >
                                      <Check size={14} />
                                    </button>
                                    <button
                                      onClick={() => handleRejectPayment(payment.id)}
                                      className="btn btn-sm btn-danger"
                                      disabled={paymentLoading || editingPaymentId !== null || isAddingPayment}
                                      title="Reject payment"
                                    >
                                      <XCircle size={14} />
                                    </button>
                                  </>
                                ) : (
                                  <>
                                    <button
                                      onClick={() => handleEditPayment(payment)}
                                      className="btn btn-sm btn-primary"
                                      disabled={paymentLoading || editingPaymentId !== null || isAddingPayment}
                                    >
                                      <Edit2 size={14} />
                                    </button>
                                    {canDeletePayments && (
                                      <button
                                        onClick={() => handleDeletePayment(payment.id)}
                                        className="btn btn-sm btn-danger"
                                        disabled={paymentLoading || editingPaymentId !== null || isAddingPayment}
                                      >
                                        <Trash2 size={14} />
                                      </button>
                                    )}
                                  </>
                                )}
                              </td>
                            )}
                          </>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <p className="no-payments">No payments recorded yet.</p>
              )}
            </div>
          )}

          {/* Status (only in edit mode) */}
          {isEditing && (
            <div className="info-row status-row">
              <span className="info-label">Status:</span>
              <select
                value={editForm.status}
                onChange={(e) => onChange({ status: e.target.value })}
                className="status-select-modern"
              >
                <option value="pending">Pending</option>
                <option value="approved">Approved</option>
                <option value="rejected">Rejected</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ReservationCard;
